import { useState, useEffect } from "react";
import { Dialog, DialogTitle, useMediaQuery } from "@mui/material";
import type { DialogProps } from "@mui/material/Dialog";
import type { SxProps, Theme } from "@mui/material/styles";
import { StyledCloseButton } from "./styled";
import { useTheme } from "@mui/material/styles";
import type { ReactNode } from "react";

interface DialogConfig {
  title?: ReactNode;
  subtitle?: ReactNode;
  hasBackdrop?: boolean;
  disableBackdropClose?: boolean;
  disableEscapeKeyDown?: boolean;
  asModal?: boolean;
  noCloseButton?: boolean;
  fullWidth?: boolean;
  /** Applied to the Dialog Paper. Pass `{ overflow: "visible" }` for dialogs containing date pickers. */
  paperSx?: SxProps<Theme>;
}

interface Props {
  config: DialogConfig;
  open: boolean;
  handleOnClose?: (() => void) | null;
  children?: ReactNode;
}

/**
 * Dialog components leveraging MUI Dialog
 */
const SbuxDialog2 = ({ config, open, handleOnClose, children }: Props) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [openDialog, setOpenDialog] = useState(false);
  const {
    title = "",
    subtitle,
    hasBackdrop = true,
    disableBackdropClose = true,
    disableEscapeKeyDown = true,
    asModal = false,
    noCloseButton = false,
    fullWidth = true,
    paperSx,
  } = config;

  useEffect(() => {
    setOpenDialog(open);
  }, [open]);

  const handleCloseEvent: DialogProps["onClose"] = (_e, reason) => {
    if (disableBackdropClose && reason === "backdropClick") {
      return;
    }

    if (typeof handleOnClose === "function") {
      handleOnClose();
    }

    setOpenDialog(false);
  };

  return (
    <Dialog
      open={openDialog}
      fullScreen={!asModal && fullScreen}
      fullWidth={fullWidth}
      onClose={handleCloseEvent}
      slotProps={{ backdrop: { invisible: !hasBackdrop } }}
      disableEscapeKeyDown={disableEscapeKeyDown}
      disableAutoFocus
      {...(paperSx ? { PaperProps: { sx: paperSx } } : {})}
    >
      <DialogTitle>
        {title} {subtitle}
      </DialogTitle>
      {!noCloseButton && (
        <StyledCloseButton
          aria-label="close"
          onClick={() =>
            handleCloseEvent({} as React.SyntheticEvent, "escapeKeyDown")
          }
        />
      )}

      {children}
    </Dialog>
  );
};

export default SbuxDialog2;
